import React from 'react';

// =============================================
// SCORECARD MODAL COMPONENT
// =============================================

export function ScorecardModal({ showScorecard, setShowScorecard, scorecardData, baseCurrencyLabel, fmtBase }) {
    if (!showScorecard || !scorecardData) return null;

    const sc = scorecardData;
    const periodLabel = sc.period ? new Date(sc.period + '-01').toLocaleString('default', { month: 'long', year: 'numeric' }) : '';
    
    const scoreColor = (s) => {
        if (s === null) return '#64748B';
        if (s >= 80) return '#10B981';
        if (s >= 60) return '#F59E0B';
        return '#EF4444';
    };
    
    const scoreEmoji = (s) => {
        if (s === null) return '—';
        if (s >= 80) return '🟢';
        if (s >= 60) return '🟡';
        return '🔴';
    };

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 300,
                background: 'rgba(0,0,0,0.75)',
                backdropFilter: 'blur(6px)',
                overflowY: 'auto',
                padding: 'max(env(safe-area-inset-top), 20px) 0 max(env(safe-area-inset-bottom), 20px)',
            }}
            onClick={() => setShowScorecard(false)}
        >
            <div
                style={{
                    background: '#0F172A',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '20px',
                    margin: '0 12px',
                    padding: '0 0 24px',
                    maxWidth: '600px',
                    marginLeft: 'auto', marginRight: 'auto',
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{
                    padding: '20px 20px 16px',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: '700', color: '#F8FAFC' }}>📊 Performance Scorecard</div>
                        <div style={{ fontSize: '13px', color: '#64748B', marginTop: '2px' }}>{periodLabel} · Progress Report</div>
                    </div>
                    <button onClick={() => setShowScorecard(false)}
                        style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', width: '32px', height: '32px', color: '#94A3B8', cursor: 'pointer', fontSize: '16px', fontFamily: 'inherit' }}>✕</button>
                </div>

                {/* Overall Score Hero */}
                <div style={{
                    padding: '24px 20px 16px',
                    display: 'flex', gap: '16px', alignItems: 'center',
                }}>
                    <div style={{
                        width: '80px', height: '80px', borderRadius: '50%', flexShrink: 0,
                        background: `conic-gradient(${scoreColor(sc.overallScore)} ${(sc.overallScore || 0) * 3.6}deg, rgba(255,255,255,0.05) 0deg)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        position: 'relative',
                    }}>
                        <div style={{
                            width: '62px', height: '62px', borderRadius: '50%',
                            background: '#0F172A',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: scoreColor(sc.overallScore), lineHeight: 1 }}>{sc.overallScore !== null ? sc.overallScore : '—'}</div>
                            {sc.overallScore !== null && <div style={{ fontSize: '9px', color: '#64748B', marginTop: '1px' }}>/ 100</div>}
                        </div>
                    </div>
                    <div>
                        <div style={{ fontSize: '22px', fontWeight: '700', color: scoreColor(sc.overallScore) }}>
                            {sc.overallScore !== null ? `${scoreEmoji(sc.overallScore)} Overall Score` : '📊 No Data Yet'}
                        </div>
                        <div style={{ fontSize: '13px', color: '#94A3B8', marginTop: '4px' }}>
                            {sc.overallScore !== null
                                ? (sc.overallScore >= 80 ? 'Strong financial performance this month.' : sc.overallScore >= 60 ? 'Good start — a few areas to improve.' : 'Attention needed in several areas.')
                                : 'Log actuals and mark loans paid to generate your scorecard.'}
                        </div>
                    </div>
                </div>

                {/* Score Pills */}
                <div style={{ display: 'flex', gap: '10px', padding: '0 20px 20px' }}>
                    {[
                        { label: 'Budget Score', score: sc.budgetScore, icon: '📦' },
                        { label: 'Loan Score', score: sc.loanScore, icon: '🏦' },
                    ].map(pill => (
                        <div key={pill.label} style={{
                            flex: 1, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
                            borderRadius: '12px', padding: '12px', textAlign: 'center',
                        }}>
                            <div style={{ fontSize: '20px' }}>{pill.icon}</div>
                            <div style={{ fontSize: '22px', fontWeight: '700', color: scoreColor(pill.score), marginTop: '4px' }}>
                                {pill.score !== null ? pill.score : '—'}
                            </div>
                            <div style={{ fontSize: '11px', color: '#64748B', marginTop: '2px' }}>{pill.label}</div>
                        </div>
                    ))}
                </div>

                {/* Budget Discipline */}
                <div style={{ padding: '0 20px 20px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#94A3B8', letterSpacing: '0.05em', marginBottom: '10px', textTransform: 'uppercase' }}>
                        📦 Budget Performance
                    </div>
                    {!sc.hasActuals ? (
                        <div style={{ padding: '14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', fontSize: '13px', color: '#64748B', textAlign: 'center' }}>
                            💡 Start logging actuals to unlock your Budget Score
                        </div>
                    ) : sc.budgetDetails.length === 0 ? (
                        <div style={{ padding: '14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', fontSize: '13px', color: '#64748B', textAlign: 'center' }}>
                            No budget categories configured.
                        </div>
                    ) : sc.budgetDetails.map((bd, i) => (
                        <div key={i} style={{
                            padding: '12px 14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)',
                            borderRadius: '10px', marginBottom: '8px',
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#F8FAFC' }}>{bd.label}</div>
                                <div style={{ fontSize: '12px', color: bd.statusColor, fontWeight: '600' }}>{bd.status}</div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#64748B', marginBottom: '8px' }}>
                                <span>Spent {bd.symbol}{Math.round(bd.spent).toLocaleString()} / {bd.symbol}{Math.round(bd.budget).toLocaleString()}</span>
                                <span style={{ color: bd.statusColor }}>{bd.pct}%</span>
                            </div>
                            <div style={{ height: '5px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                                <div style={{
                                    height: '100%', borderRadius: '3px',
                                    width: `${Math.min(100, bd.pct)}%`,
                                    background: bd.pct > 100 ? '#EF4444' : bd.pct > 90 ? '#F59E0B' : '#10B981',
                                    transition: 'width 0.5s ease',
                                }} />
                            </div>
                        </div>
                    ))}
                </div>

                {/* Loan Repayment */}
                <div style={{ padding: '0 20px 20px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#94A3B8', letterSpacing: '0.05em', marginBottom: '10px', textTransform: 'uppercase' }}>
                        🏦 Loan Repayments
                    </div>
                    {sc.loanDetails.length === 0 ? (
                        <div style={{ padding: '14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', fontSize: '13px', color: '#64748B', textAlign: 'center' }}>
                            No active loans tracked.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {sc.loanDetails.map((ld, i) => (
                                <div key={i} style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '10px 14px', background: 'rgba(255,255,255,0.03)',
                                    border: '1px solid rgba(255,255,255,0.05)', borderRadius: '10px',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '16px' }}>{ld.paid ? '✅' : '⚠️'}</span>
                                        <div>
                                            <div style={{ fontSize: '13px', fontWeight: '500', color: '#F8FAFC' }}>{ld.name}</div>
                                            {ld.date && <div style={{ fontSize: '11px', color: '#64748B' }}>{ld.date}</div>}
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        {ld.paid
                                            ? <span style={{ fontSize: '12px', color: '#10B981', fontWeight: '600' }}>{ld.partial ? '⚡ Partial' : 'Paid'}</span>
                                            : <span style={{ fontSize: '12px', color: '#F59E0B' }}>Not recorded</span>
                                        }
                                    </div>
                                </div>
                            ))}
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 14px 0', fontSize: '12px', color: '#64748B' }}>
                                <span>Paid: {sc.loansMarkedPaid} / {sc.loansTotal}</span>
                                <span style={{ color: scoreColor(sc.loanScore), fontWeight: '600' }}>Score: {sc.loanScore !== null ? sc.loanScore : '—'}/100</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Net Cash Position */}
                {sc.hasActuals && (
                    <div style={{ padding: '0 20px 20px' }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#94A3B8', letterSpacing: '0.05em', marginBottom: '10px', textTransform: 'uppercase' }}>
                            💰 Cash Flow Summary ({baseCurrencyLabel})
                        </div>
                        <div style={{
                            padding: '14px', background: 'rgba(255,255,255,0.03)',
                            border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px',
                        }}>
                            {[
                                { label: 'Total Budgeted', value: sc.totalBudgeted, color: '#94A3B8' },
                                { label: 'Total Spent', value: sc.totalSpent, color: '#F8FAFC' },
                                { label: 'Loan Repayments', value: sc.totalLoanRepayments, color: '#F8FAFC' },
                                { label: 'Total Outflows', value: sc.totalOutflows, color: '#EF4444', bold: true, separator: true },
                            ].map((row, i) => (
                                <div key={i}>
                                    {row.separator && <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', margin: '8px 0' }} />}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                        <span style={{ fontSize: '13px', color: '#64748B' }}>{row.label}</span>
                                        <span style={{ fontSize: '13px', color: row.color, fontWeight: row.bold ? '700' : '500' }}>
                                            {fmtBase(row.value)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                            <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', margin: '8px 0 6px' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '13px', color: '#64748B' }}>Variance (budget vs spent)</span>
                                <span style={{ fontSize: '13px', fontWeight: '700', color: sc.netVariance >= 0 ? '#10B981' : '#EF4444' }}>
                                    {sc.netVariance >= 0 ? '+ ' : ''}{fmtBase(sc.netVariance)} {sc.netVariance >= 0 ? '🟢' : '🔴'}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Behavioural Insights */}
                <div style={{ padding: '0 20px 20px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#94A3B8', letterSpacing: '0.05em', marginBottom: '10px', textTransform: 'uppercase' }}>
                        💬 Behavioural Insights
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {sc.insights.map((ins, i) => (
                            <div key={i} style={{
                                padding: '12px 14px',
                                background: ins.type === 'alert' ? 'rgba(239,68,68,0.08)' : ins.type === 'nudge' ? 'rgba(245,158,11,0.08)' : ins.type === 'gold-warn' ? 'rgba(255,215,0,0.08)' : 'rgba(16,185,129,0.08)',
                                border: `1px solid ${ins.type === 'alert' ? 'rgba(239,68,68,0.2)' : ins.type === 'nudge' ? 'rgba(245,158,11,0.2)' : ins.type === 'gold-warn' ? 'rgba(255,215,0,0.2)' : 'rgba(16,185,129,0.2)'}`,
                                borderRadius: '10px',
                                fontSize: '13px',
                                color: '#F8FAFC',
                                lineHeight: '1.5',
                            }}>
                                <span style={{ marginRight: '6px' }}>{ins.emoji}</span>{ins.msg}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Performance Trend */}
                {sc.recentSnapshots.length >= 2 && (
                    <div style={{ padding: '0 20px 20px' }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#94A3B8', letterSpacing: '0.05em', marginBottom: '10px', textTransform: 'uppercase' }}>
                            📈 Performance Trend
                        </div>
                        <div style={{
                            padding: '16px', background: 'rgba(255,255,255,0.03)',
                            border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px',
                        }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', height: '60px', marginBottom: '8px' }}>
                                {sc.recentSnapshots.map((snap, i) => {
                                    const h = snap.overallScore ? Math.max(4, Math.round((snap.overallScore / 100) * 56)) : 4;
                                    return (
                                        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                            <div style={{ fontSize: '10px', color: scoreColor(snap.overallScore), fontWeight: '600' }}>{snap.overallScore || '—'}</div>
                                            <div style={{
                                                width: '100%', height: `${h}px`,
                                                background: scoreColor(snap.overallScore),
                                                borderRadius: '3px 3px 0 0', opacity: 0.8,
                                            }} />
                                        </div>
                                    );
                                })}
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                {sc.recentSnapshots.map((snap, i) => (
                                    <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: '10px', color: '#64748B' }}>
                                        {snap.period ? new Date(snap.period + '-01').toLocaleString('default', { month: 'short' }) : ''}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Footer note */}
                <div style={{ padding: '0 20px', fontSize: '11px', color: '#475569', textAlign: 'center', lineHeight: '1.5' }}>
                    Scores are advisory &amp; motivational only — not financial advice. Report auto-saved to your monthly history.
                </div>
            </div>
        </div>
    );
}
